import React, { useEffect, useState } from "react";
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster } from "components/common/toast";
import { StartPauseAPI } from "network/bookings";
import { errText } from "./errText";
import { pauseMinutes, pauseRetry, pauseWindow } from "./settingsLogic";
import PhonePreview from "./settingsPreview";

// Pause services: blocco immediato delle prenotazioni online, tutti i servizi o alcuni, a tempo o fino a fine giornata.
const blank = () => ({ mode: "all", shifts: [], hours: 1, minutes: 0, eod: false, message: "" });

const PauseModal = ({ isOpen, shifts, bandLabel, onClose, onSaved }) => {
  const { t } = useTranslation("bookings");
  const [f, setF] = useState(blank());
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    if (!isOpen) return undefined;
    setF(blank());
    setTried(false);
    setNow(new Date());
    // la riga "Dalle ... fino alle ..." segue l'orologio
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [isOpen]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (id) => set("shifts", f.shifts.includes(id) ? f.shifts.filter((x) => x !== id) : [...f.shifts, id]);
  const minutes = pauseMinutes(f.hours, f.minutes);
  const win = pauseWindow(now, minutes, f.eod);
  const errShifts = f.mode === "specific" && !f.shifts.length;
  const errDuration = !win;

  const save = async () => {
    setTried(true);
    if (errShifts || errDuration) return;
    setBusy(true);
    try {
      await StartPauseAPI({
        mode: f.mode, shifts: f.mode === "all" ? [] : f.shifts, minutes,
        until_end_of_day: f.eod, message: f.message.trim(),
      });
      successToaster(t("settings.pauseOn"));
      onSaved();
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  // anteprima: con servizi specifici il cliente vede comunque il blocco su quei turni
  const block = { kind: "pause", message: f.message.trim(), retry: win ? pauseRetry(win.until) : null };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>{t("settings.pauseTitle")}</ModalHeader>
      <ModalBody>
        <Row>
          <Col md="7">
            <p className="small text-muted">{t("settings.pauseIntro")}</p>
            <div><label><input type="radio" checked={f.mode === "all"} onChange={() => set("mode", "all")} /> {t("settings.allServices")}</label></div>
            <div><label><input type="radio" checked={f.mode === "specific"} onChange={() => set("mode", "specific")} /> {t("settings.someServices")}</label></div>
            {f.mode === "specific" && (
              <div className="ml-4">
                {shifts.length === 0 && <small className="text-muted">{t("settings.noBands")}</small>}
                {shifts.map((s) => (
                  <label key={s.id} className="d-block mb-1">
                    <input type="checkbox" checked={f.shifts.includes(s.id)} onChange={() => toggle(s.id)} /> {bandLabel(s)} <span className="text-muted">{s.start_time} - {s.end_time || s.last_seating}</span>
                  </label>
                ))}
              </div>
            )}
            {tried && errShifts && <small className="text-danger d-block">{t("settings.errPickService")}</small>}

            <Label className="mt-3 d-block font-weight-bold">{t("settings.pauseFor")}</Label>
            <div className="d-flex align-items-center flex-wrap">
              <Input type="select" disabled={f.eod} value={f.hours} onChange={(e) => set("hours", Number(e.target.value))} style={{ maxWidth: 80 }}>
                {Array.from({ length: 13 }, (_, i) => <option key={i} value={i}>{i}</option>)}
              </Input>
              <span className="mx-2">{t("settings.hours")}</span>
              <Input type="select" disabled={f.eod} value={f.minutes} onChange={(e) => set("minutes", Number(e.target.value))} style={{ maxWidth: 80 }}>
                {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{m}</option>)}
              </Input>
              <span className="mx-2">{t("settings.minutes")}</span>
            </div>
            <label className="mt-2 d-block"><input type="checkbox" checked={f.eod} onChange={(e) => set("eod", e.target.checked)} /> {t("settings.untilEod")}</label>
            {win ? (
              <small className="text-muted d-block">
                {win.endOfDay ? t("settings.pauseLineEod", { from: win.from }) : t("settings.pauseLine", { from: win.from, to: win.to })}
              </small>
            ) : (
              tried && <small className="text-danger d-block">{t("settings.errDuration")}</small>
            )}

            <Label className="mt-3 d-block font-weight-bold">{t("settings.notifyMessage")}</Label>
            <Input type="textarea" rows="3" maxLength={200} placeholder={t("settings.previewDefaultPause")} value={f.message} onChange={(e) => set("message", e.target.value)} />
            <small className="text-muted">{f.message.length}/200</small>
          </Col>
          <Col md="5" className="mt-3 mt-md-0">
            <div className="small text-muted text-center mb-2">{t("settings.previewTitle")}</div>
            <PhonePreview block={block} compact />
            {f.mode === "specific" && <small className="text-muted d-block text-center mt-2">{t("settings.previewSpecific")}</small>}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>{t("settings.discard")}</Button>
        <Button color="primary" disabled={busy} onClick={save}>{t("save")}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default PauseModal;
